import type {FC} from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from 'recharts';
import type {AssistantChartResponse} from 'src/api/ProactApiClient';
import {AssistantChartTypeEnum} from 'src/api/ProactApiClient';
import type {ChatChartLayout} from 'src/app/assistant/Chart/chatChartFormat';
import {
  CHART_X_KEY,
  CHAT_PIE_SIZE,
  TICK_FONT_PX,
  TILTED_LABEL_ANGLE,
  chartDataRows,
  chartSeriesKey,
  chartValueAt,
  drawablePieSlices,
  formatChartAxisTick,
  formatChartValue,
  truncateChartLabel,
} from 'src/app/assistant/Chart/chatChartFormat';
import type {PrintChartPlan} from './printChartPlan';
import {PRINT_CHART_WIDTH} from './printChartPlan';

export interface TranscriptPrintChartPalette {
  dimmed: string;
  border: string;
  paper: string;
  chartColors: readonly string[];
}

const Swatch: FC<{color: string}> = ({color}) => (
  <svg width={10} height={10} aria-hidden="true" className="swatch">
    <rect width={10} height={10} rx={2} fill={color} />
  </svg>
);

const PrintPie: FC<{
  chart: AssistantChartResponse;
  palette: TranscriptPrintChartPalette;
}> = ({chart, palette}) => {
  const slices = chart.points.map((point, index) => ({
    name: point.x,
    value: chartValueAt(point, 0),
    color: palette.chartColors[index],
  }));
  const drawable = drawablePieSlices(slices);
  return (
    <div className="pie">
      <PieChart width={CHAT_PIE_SIZE} height={CHAT_PIE_SIZE}>
        <Pie
          data={drawable}
          dataKey="value"
          nameKey="name"
          outerRadius={CHAT_PIE_SIZE / 2 - 4}
          stroke={palette.paper}
          strokeWidth={1}
          isAnimationActive={false}>
          {drawable.map((slice, index) => (
            <Cell key={index} fill={slice.color} />
          ))}
        </Pie>
      </PieChart>
      <ul className="pie-legend">
        {slices.map((slice, index) => (
          <li key={index}>
            <span className="legend-name">
              <Swatch color={slice.color} />
              {slice.name}
            </span>
            <span className="num">{formatChartValue(slice.value, chart.unit)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PrintCartesian: FC<{
  chart: AssistantChartResponse;
  layout: ChatChartLayout;
  svgHeight: number;
  palette: TranscriptPrintChartPalette;
}> = ({chart, layout, svgHeight, palette}) => {
  const data = chartDataRows(chart);
  const tick = {fontSize: TICK_FONT_PX, fill: palette.dimmed};
  const valueAxis = {
    type: 'number' as const,
    tickFormatter: (value: number) => formatChartAxisTick(value, chart.unit),
    allowDecimals: !layout.integerTicks,
    axisLine: false,
    tickLine: {stroke: palette.border},
    tick,
  };
  const grid = (
    <CartesianGrid
      strokeDasharray="5 5"
      stroke={palette.border}
      vertical={layout.kind === 'rows'}
      horizontal={layout.kind !== 'rows'}
    />
  );
  const size = {width: PRINT_CHART_WIDTH, height: svgHeight, data};
  const color = (index: number) => palette.chartColors[index];
  const bars = chart.series.map((_, index) => (
    <Bar
      key={index}
      dataKey={chartSeriesKey(index)}
      fill={color(index)}
      stackId={
        chart.type === AssistantChartTypeEnum.StackedBar ? 'stack' : undefined
      }
      isAnimationActive={false}
    />
  ));

  if (layout.kind === 'rows') {
    return (
      <BarChart {...size} layout="vertical">
        {grid}
        <XAxis {...valueAxis} />
        <YAxis
          type="category"
          dataKey={CHART_X_KEY}
          width={layout.categoryAxisWidth}
          interval={0}
          axisLine={false}
          tickLine={false}
          // Recharts word-wraps a tick given a width; the label is already cut to one line.
          tick={{...tick, width: undefined}}
          tickFormatter={(label: string) =>
            truncateChartLabel(label, layout.labelMaxChars)
          }
        />
        {bars}
      </BarChart>
    );
  }

  const {tilt} = layout;
  const tiltedTick = {...tick, angle: TILTED_LABEL_ANGLE, textAnchor: 'end' as const};
  const categoryAxis = (
    <XAxis
      dataKey={CHART_X_KEY}
      interval="preserveStartEnd"
      minTickGap={5}
      axisLine={false}
      tickLine={false}
      {...(tilt
        ? {
            height: tilt.axisHeight,
            angle: TILTED_LABEL_ANGLE,
            tick: tiltedTick,
            tickFormatter: (label: string) =>
              truncateChartLabel(label, tilt.labelMaxChars),
          }
        : {tick})}
    />
  );
  const margin = {top: 5, right: 5, bottom: 5, left: tilt?.leftMargin ?? 5};
  const axes = (
    <>
      {grid}
      {categoryAxis}
      <YAxis {...valueAxis} />
    </>
  );

  switch (chart.type) {
    case AssistantChartTypeEnum.Line:
      return (
        <LineChart {...size} margin={margin}>
          {axes}
          {chart.series.map((_, index) => (
            <Line
              key={index}
              type="linear"
              dataKey={chartSeriesKey(index)}
              stroke={color(index)}
              strokeWidth={2}
              dot={{r: 3, fill: color(index), stroke: color(index)}}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      );
    case AssistantChartTypeEnum.Area:
      return (
        <AreaChart {...size} margin={margin}>
          {axes}
          {chart.series.map((_, index) => (
            <Area
              key={index}
              type="linear"
              dataKey={chartSeriesKey(index)}
              stroke={color(index)}
              strokeWidth={2}
              fill={color(index)}
              fillOpacity={0.2}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      );
    default:
      return (
        <BarChart {...size} margin={margin}>
          {axes}
          {bars}
        </BarChart>
      );
  }
};

export interface TranscriptPrintChartProps {
  plan: Extract<PrintChartPlan, {kind: 'pie' | 'cartesian'}>;
  palette: TranscriptPrintChartPalette;
}

/** Fixed-size, unanimated recharts drawing for the print iframe (no tooltip, no CSS variables). */
export const TranscriptPrintChart: FC<TranscriptPrintChartProps> = ({
  plan,
  palette,
}) => {
  if (plan.kind === 'pie') {
    return <PrintPie chart={plan.chart} palette={palette} />;
  }
  return (
    <>
      <PrintCartesian
        chart={plan.chart}
        layout={plan.layout}
        svgHeight={plan.svgHeight}
        palette={palette}
      />
      {plan.chart.series.length >= 2 && (
        <ul className="chart-legend">
          {plan.chart.series.map((series, index) => (
            <li key={index}>
              <Swatch color={palette.chartColors[index]} />
              {series.label}
            </li>
          ))}
        </ul>
      )}
    </>
  );
};
